// The browser's copy of every synced table. Components read it with useRows/useRow, which never
// wait on the network. Server changes stream in; local writes show instantly as "overlays" until
// the server confirms them (see mutate.ts).
import { useSyncExternalStore } from "react";
import type { SyncedTables } from "../../../shared/db.gen.ts";
import { clearCache, loadCache, queueChanges, replaceCache } from "./idb.ts";

export type TableName = keyof SyncedTables & string;
type Row = { id: string } & Record<string, unknown>;
export type Change = { t: string; id: string; row: Row | null };
export type SyncStatus = "loading" | "live" | "reconnecting";

declare const __BUILD_ID__: string;

class Store {
  private base = new Map<string, Map<string, Row>>();
  private overlays: { key: number; changes: Change[]; waitFor: number | null }[] = [];
  private revs = new Map<string, number>();
  private views = new Map<string, { rev: number; rows: Row[]; byId: Map<string, Row> }>();
  private listeners = new Set<() => void>();
  private nextKey = 1;
  version = 0;
  schema = "";
  status: SyncStatus = "loading";
  ready = false;
  newBuild = false;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private emit() {
    for (const fn of this.listeners) fn();
  }

  private bump(t: string) {
    this.revs.set(t, (this.revs.get(t) ?? 0) + 1);
  }

  private view(t: string) {
    const rev = this.revs.get(t) ?? 0;
    let v = this.views.get(t);
    if (!v || v.rev !== rev) {
      const byId = new Map(this.base.get(t));
      for (const o of this.overlays) for (const c of o.changes) if (c.t === t) c.row ? byId.set(c.id, c.row) : byId.delete(c.id);
      v = { rev, rows: [...byId.values()], byId };
      this.views.set(t, v);
    }
    return v;
  }

  rows(t: string): Row[] {
    return this.view(t).rows;
  }

  row(t: string, id: string | null | undefined): Row | undefined {
    return id ? this.view(t).byId.get(id) : undefined;
  }

  load(tables: Record<string, Row[]>, version: number, schema: string) {
    this.base = new Map(Object.entries(tables).map(([t, rows]) => [t, new Map(rows.map((r) => [r.id, r]))]));
    for (const t of new Set([...this.revs.keys(), ...this.base.keys()])) this.bump(t);
    this.version = version;
    this.schema = schema;
    this.ready = true;
    this.emit();
  }

  applyServer(changes: Change[], to: number) {
    for (const c of changes) {
      let table = this.base.get(c.t);
      if (!table) this.base.set(c.t, (table = new Map()));
      c.row ? table.set(c.id, c.row) : table.delete(c.id);
      this.bump(c.t);
    }
    this.version = to;
    this.settle();
    this.emit();
  }

  addOverlay(changes: Change[]): number {
    const key = this.nextKey++;
    this.overlays.push({ key, changes, waitFor: null });
    for (const c of changes) this.bump(c.t);
    this.emit();
    return key;
  }

  /** The server committed the write at `version`; drop the overlay once we've seen that version. */
  confirmOverlay(key: number, version: number) {
    const o = this.overlays.find((x) => x.key === key);
    if (o) o.waitFor = version;
    if (this.settle()) this.emit();
  }

  dropOverlay(key: number) {
    const o = this.overlays.find((x) => x.key === key);
    if (!o) return;
    this.overlays = this.overlays.filter((x) => x !== o);
    for (const c of o.changes) this.bump(c.t);
    this.emit();
  }

  private settle(): boolean {
    const keep = this.overlays.filter((o) => o.waitFor === null || o.waitFor > this.version);
    if (keep.length === this.overlays.length) return false;
    for (const o of this.overlays) if (!keep.includes(o)) for (const c of o.changes) this.bump(c.t);
    this.overlays = keep;
    return true;
  }

  setStatus(status: SyncStatus) {
    if (status === this.status) return;
    this.status = status;
    this.emit();
  }

  markNewBuild() {
    this.newBuild = true;
    this.emit();
  }
}

export const store = new Store();

export function useRows<T extends TableName>(table: T): SyncedTables[T][] {
  return useSyncExternalStore(store.subscribe, () => store.rows(table)) as unknown as SyncedTables[T][];
}

export function useRow<T extends TableName>(table: T, id: string | null | undefined): SyncedTables[T] | undefined {
  return useSyncExternalStore(store.subscribe, () => store.row(table, id)) as unknown as SyncedTables[T] | undefined;
}

export function useSyncState() {
  const status = useSyncExternalStore(store.subscribe, () => store.status);
  const version = useSyncExternalStore(store.subscribe, () => store.version);
  const newBuild = useSyncExternalStore(store.subscribe, () => store.newBuild);
  const ready = useSyncExternalStore(store.subscribe, () => store.ready);
  return { status, version, newBuild, ready };
}

// Connection -------------------------------------------------------------------------------------

async function snapshot() {
  const res = await fetch("/api/sync/snapshot");
  if (res.status === 401) return location.assign("/login");
  if (!res.ok) throw new Error(`snapshot failed (${res.status})`);
  const data = (await res.json()) as { version: number; schema: string; tables: Record<string, Row[]> };
  store.load(data.tables, data.version, data.schema);
  replaceCache(data.tables, { version: data.version, schema: data.schema }).catch(() => {});
}

let source: EventSource | null = null;
let retryMs = 500;

function openStream() {
  source?.close();
  const es = new EventSource(`/api/sync/stream?since=${store.version}&schema=${encodeURIComponent(store.schema)}`);
  source = es;
  es.addEventListener("hello", (e) => {
    retryMs = 500;
    store.setStatus("live");
    const { build } = JSON.parse((e as MessageEvent).data) as { build: string };
    if (build && __BUILD_ID__ && build !== __BUILD_ID__) store.markNewBuild();
  });
  es.addEventListener("changes", (e) => {
    const { to, changes } = JSON.parse((e as MessageEvent).data) as { to: number; changes: Change[] };
    store.applyServer(changes, to);
    queueChanges(changes, { version: to, schema: store.schema });
  });
  es.addEventListener("reset", async () => {
    es.close();
    await clearCache().catch(() => {});
    await snapshot();
    openStream();
  });
  es.onerror = () => {
    es.close();
    store.setStatus("reconnecting");
    // A 401 also lands here; the snapshot check on reconnect sends the user to sign in.
    setTimeout(async () => {
      const me = await fetch("/api/me").catch(() => null);
      if (me?.status === 401) return location.assign("/login");
      openStream();
    }, retryMs);
    retryMs = Math.min(retryMs * 2, 10_000);
  };
}

export async function startSync() {
  const cached = await loadCache().catch(() => null);
  if (cached) store.load(cached.tables, cached.version, cached.schema);
  else await snapshot();
  openStream();
}

export async function resetLocalData() {
  source?.close();
  await clearCache().catch(() => {});
}
